import { useMemo, useState } from "react";
import type { Invoice, Job } from "@clevis/shared-types";
import { Card, CardTitle, cn } from "@clevis/shared-ui";
import { downloadCsv, csvMoney } from "../lib/csv";

// Reports → Sales by rep / by department: what each rep sold (invoices they
// own) and what each department billed (through the invoice's job), billed
// in the period and collected in the period. A rep who is let into Reports
// sees only their own line.

const money = (c: number) => (c / 100).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

interface Row {
  key: string;
  name: string;
  invoices: number;
  billedCents: number;
  collectedCents: number;
  openCents: number;
}

export function SalesByCard({
  invoices,
  jobs,
  members,
  departments,
  start,
  end,
  rangeLabel,
  ownOnlyUid,
}: {
  invoices: Array<Invoice & { id: string }>;
  jobs: Array<Job & { id: string }>;
  members: Record<string, string>;
  departments: Array<{ id: string; name: string }>;
  start: number;
  end: number;
  rangeLabel: string;
  /** A rep (not a manager): only their own line, no department view. */
  ownOnlyUid?: string | null;
}) {
  const [by, setBy] = useState<"rep" | "department">("rep");
  const mode = ownOnlyUid ? "rep" : by;

  const rows = useMemo(() => {
    const deptOfJob = new Map(jobs.map((j) => [j.id, j.departmentId ?? null]));
    const deptName = (id: string | null) => (id ? (departments.find((d) => d.id === id)?.name ?? "Department") : "No department");
    const out = new Map<string, Row>();
    const row = (key: string, name: string) => out.get(key) ?? out.set(key, { key, name, invoices: 0, billedCents: 0, collectedCents: 0, openCents: 0 }).get(key)!;
    for (const inv of invoices) {
      if (inv.status === "draft" || inv.status === "void") continue;
      const key = mode === "rep" ? (inv.salespersonUid ?? "") : (deptOfJob.get(inv.jobId ?? "") ?? "");
      if (ownOnlyUid && key !== ownOnlyUid) continue;
      const name = mode === "rep" ? (inv.salespersonUid ? inv.salespersonName || members[inv.salespersonUid] || "Rep" : "No rep") : deptName(key || null);
      const r = row(key || "-", name);
      const inRange = inv.createdAt >= start && inv.createdAt < end;
      if (inRange) {
        r.invoices += 1;
        r.billedCents += inv.totalCents;
        r.openCents += Math.max(0, inv.totalCents - inv.amountPaidCents);
      }
      for (const p of inv.payments ?? []) if (p.at >= start && p.at < end) r.collectedCents += p.amountCents;
    }
    return [...out.values()].filter((r) => r.invoices || r.collectedCents).sort((a, b) => b.billedCents - a.billedCents || b.collectedCents - a.collectedCents);
  }, [invoices, jobs, members, departments, start, end, mode, ownOnlyUid]);

  const total = rows.reduce((s, r) => ({ billed: s.billed + r.billedCents, collected: s.collected + r.collectedCents }), { billed: 0, collected: 0 });

  return (
    <Card>
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <CardTitle className="mb-0">Sales by {mode === "rep" ? "rep" : "department"} · {rangeLabel}</CardTitle>
        <div className="flex items-center gap-2">
          {!ownOnlyUid ? (
            <div className="flex rounded-lg border border-steel/30 p-0.5 text-xs">
              {(["rep", "department"] as const).map((k) => (
                <button key={k} className={cn("rounded-md px-2 py-0.5 capitalize", by === k ? "bg-ink text-white" : "text-steel")} onClick={() => setBy(k)}>
                  {k}
                </button>
              ))}
            </div>
          ) : null}
          {rows.length ? (
            <button
              className="text-xs text-steel hover:text-ink"
              onClick={() =>
                downloadCsv(
                  `sales-by-${mode}`,
                  [mode === "rep" ? "Rep" : "Department", "Invoices", "Billed", "Collected", "Still open"],
                  rows.map((r) => [r.name, r.invoices, csvMoney(r.billedCents), csvMoney(r.collectedCents), csvMoney(r.openCents)]),
                )
              }
            >
              Export CSV
            </button>
          ) : null}
        </div>
      </div>
      <p className="mb-2 text-xs text-steel">
        {mode === "rep" ? "By the estimate's owner, carried onto the invoice." : "By the department of the invoice's job."} Billed = invoices made in the period;
        collected = payments that landed in it.
      </p>
      {rows.length === 0 ? (
        <p className="text-xs text-steel">Nothing in this period.</p>
      ) : (
        <div className="text-xs">
          <div className="grid grid-cols-[minmax(0,1fr)_3.5rem_5rem_5rem_5rem] gap-2 border-b border-steel/15 pb-1 text-steel">
            <span>{mode === "rep" ? "Rep" : "Department"}</span>
            <span className="text-right">Inv.</span>
            <span className="text-right">Billed</span>
            <span className="text-right">Collected</span>
            <span className="text-right">Open</span>
          </div>
          {rows.map((r) => (
            <div key={r.key} className="grid grid-cols-[minmax(0,1fr)_3.5rem_5rem_5rem_5rem] gap-2 py-1 text-ink">
              <span className="truncate">{r.name}</span>
              <span className="text-right">{r.invoices}</span>
              <span className="text-right font-semibold">{money(r.billedCents)}</span>
              <span className="text-right">{money(r.collectedCents)}</span>
              <span className={cn("text-right", r.openCents ? "text-brandorange-dark" : "text-steel")}>{money(r.openCents)}</span>
            </div>
          ))}
          <div className="grid grid-cols-[minmax(0,1fr)_3.5rem_5rem_5rem_5rem] gap-2 border-t border-steel/15 pt-1 text-ink">
            <span className="text-steel">Total</span>
            <span />
            <span className="text-right font-bold">{money(total.billed)}</span>
            <span className="text-right font-bold">{money(total.collected)}</span>
            <span />
          </div>
        </div>
      )}
    </Card>
  );
}
